import { Box, ClickAwayListener, MenuItem, Select, Typography, useTheme } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { STATUS_ICON_COLOR_MAPPING } from '../constants';
import { StatusDot } from './StatusIcon';
import { useUpdateProjectStatusMutation } from '../api/adminProjectsApiSlice';

interface ProjectStatusCellProps {
  projectId: string;
  status: string;
  allowedTransitions?: string[];
  editingStatusProjectId: string | null;
  setEditingStatusProjectId: (id: string | null) => void;
}

export const ProjectStatusCell = ({
  projectId,
  status,
  allowedTransitions,
  editingStatusProjectId,
  setEditingStatusProjectId,
}: ProjectStatusCellProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const [updateProjectStatus, { isLoading: isUpdateProjectStatusLoading }] =
    useUpdateProjectStatusMutation();

  const isEditing = editingStatusProjectId === projectId;
  const [menuOpen, setMenuOpen] = useState(false);

  const handleStatusChange = async (event: SelectChangeEvent<string>) => {
    const newStatus = event.target.value;

    try {
      await updateProjectStatus({ projectId, status: newStatus }).unwrap();
      setEditingStatusProjectId(null);
    } catch (e) {
      console.error('Status update failed', e);
    }
  };
  if (isUpdateProjectStatusLoading) {
    return (
      <Typography variant="body1">
        {formatMessage({ id: 'adminProjects.content.table.status.updateLoading' })}
      </Typography>
    );
  }
  if (isEditing) {
    return (
      <ClickAwayListener
        onClickAway={() => {
          if (!menuOpen) setEditingStatusProjectId(null);
        }}
      >
        <Select
          size="small"
          value={status}
          open={menuOpen}
          onOpen={() => setMenuOpen(true)}
          onChange={handleStatusChange}
          onClose={() => {
            setMenuOpen(false);
            setEditingStatusProjectId(null);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value={status} disabled>
            {formatMessage({
              id: `adminProjects.content.table.status.${status.toLowerCase()}`,
            })}
          </MenuItem>

          {allowedTransitions?.map((s) => (
            <MenuItem key={s} value={s}>
              {formatMessage({
                id: `adminProjects.content.table.status.${s.toLowerCase()}`,
              })}
            </MenuItem>
          ))}
        </Select>
      </ClickAwayListener>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        justifyContent: 'center',
      }}
    >
      <StatusDot
        color={STATUS_ICON_COLOR_MAPPING(theme)[status as keyof typeof STATUS_ICON_COLOR_MAPPING]}
      />
      <Typography variant="body2" color="text.secondary">
        {formatMessage({
          id: `adminProjects.content.table.status.${status.toLowerCase()}`,
        })}
      </Typography>
    </Box>
  );
};
